import { baseApi } from './baseApi';
import type { AdDetailWithPoster, AdWithRelations, CommentWithUser } from '@nimbazaar/types';
import { API_ENDPOINTS } from '@nimbazaar/constants';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface PaginatedApiResponse<T> extends ApiResponse<T> {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface RelatedAd extends Omit<AdWithRelations, 'user'> {
  user: { id: string; name: string; avatar?: string };
  bidCount: number;
}

interface CreateBidRequest {
  adId: string;
  price: number;
  description: string;
}

interface CreateCommentRequest {
  adId: string;
  content: string;
  parentId?: string;
}

export interface CreateAdRequest {
  title: string;
  description: string;
  categoryId: string;
  minBudget?: number;
  maxBudget?: number;
  location?: string;
  images?: string[];
}

export interface UpdateAdRequest {
  id: string;
  title?: string;
  description?: string;
  categoryId?: string;
  minBudget?: number | null;
  maxBudget?: number | null;
  location?: string | null;
  images?: string[];
}

export interface GetAdsParams {
  page?: number;
  limit?: number;
  categoryId?: string;
  search?: string;
  location?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface AdListItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  categoryId: string;
  minBudget?: number;
  maxBudget?: number;
  location?: string;
  status: string;
  viewCount: number;
  createdAt: string;
  expiresAt: string;
  category: { id: string; name: string; slug: string };
  images: { id: string; url: string; sortOrder: number }[];
  user: { id: string; name: string; avatar?: string };
  bidCount: number;
}

export const adsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdBySlug: builder.query<ApiResponse<AdDetailWithPoster>, string>({
      query: (slug) => API_ENDPOINTS.ADS.BY_SLUG(slug),
      providesTags: (result, error, slug) => [{ type: 'Ad', id: slug }],
    }),
    getRelatedAds: builder.query<ApiResponse<RelatedAd[]>, { adId: string; limit?: number }>({
      query: ({ adId, limit = 6 }) => `${API_ENDPOINTS.ADS.DETAIL(adId)}/related?limit=${limit}`,
      providesTags: ['Ads'],
    }),
    getAds: builder.query<PaginatedApiResponse<AdListItem[]>, GetAdsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
        if (params?.search) searchParams.set('search', params.search);
        if (params?.location) searchParams.set('location', params.location);
        if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
        if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
        const qs = searchParams.toString();
        return `${API_ENDPOINTS.ADS.BASE}${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Ads'],
    }),
    getMyAds: builder.query<PaginatedApiResponse<AdListItem[]>, { page?: number; limit?: number } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        const qs = searchParams.toString();
        return `${API_ENDPOINTS.ADS.MY_ADS}${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Ads'],
    }),
    createAd: builder.mutation<ApiResponse<AdListItem>, CreateAdRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.ADS.BASE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Ads', 'Categories', 'User', 'Transactions'],
    }),
    updateAd: builder.mutation<ApiResponse<AdListItem>, UpdateAdRequest>({
      query: ({ id, ...body }) => ({
        url: API_ENDPOINTS.ADS.DETAIL(id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Ads', 'Ad', 'Categories'],
    }),
    checkContactUnlock: builder.query<ApiResponse<{ unlocked: boolean }>, string>({
      query: (adId) => API_ENDPOINTS.ADS.CHECK_CONTACT_UNLOCK(adId),
    }),
    unlockContact: builder.mutation<ApiResponse<{ unlocked: boolean }>, string>({
      query: (adId) => ({
        url: API_ENDPOINTS.ADS.UNLOCK_CONTACT(adId),
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Transactions'],
    }),
    closeAd: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ADS.CLOSE(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ads', 'Ad', 'Categories', 'Bids'],
    }),
    reopenAd: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ADS.REOPEN(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ads', 'Ad', 'Categories', 'Bids'],
    }),
    toggleBidsVisibility: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ADS.TOGGLE_BIDS_VISIBILITY(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ads', 'Ad'],
    }),
    createBid: builder.mutation<ApiResponse<unknown>, CreateBidRequest>({
      query: ({ adId, ...body }) => ({
        url: API_ENDPOINTS.BIDS.CREATE(adId),
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Ad' }, 'Bids'],
    }),
    acceptBid: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.BIDS.ACCEPT(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ad', 'Ads', 'Bids', 'Categories'],
    }),
    rejectBid: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.BIDS.REJECT(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ad', 'Ads', 'Bids'],
    }),
    withdrawBid: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: API_ENDPOINTS.BIDS.WITHDRAW(id),
        method: 'POST',
      }),
      invalidatesTags: ['Ad', 'Ads', 'Bids'],
    }),
    getMyBids: builder.query<PaginatedApiResponse<unknown[]>, { page?: number; limit?: number; status?: string } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.status) searchParams.set('status', params.status);
        const qs = searchParams.toString();
        return `${API_ENDPOINTS.BIDS.MY_BIDS}${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Bids'],
    }),
    updateComment: builder.mutation<ApiResponse<CommentWithUser>, { id: string; adId: string; content: string }>({
      query: ({ id, content }) => ({
        url: API_ENDPOINTS.COMMENTS.DETAIL(id),
        method: 'PATCH',
        body: { content },
      }),
      invalidatesTags: (result, error, { adId }) => [{ type: 'Comments', id: adId }],
    }),
    deleteComment: builder.mutation<ApiResponse<unknown>, { id: string; adId: string }>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.COMMENTS.DETAIL(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { adId }) => [{ type: 'Comments', id: adId }],
    }),
    reactToComment: builder.mutation<ApiResponse<{ action: string; emoji: string }>, { id: string; adId: string; emoji: string }>({
      query: ({ id, emoji }) => ({
        url: API_ENDPOINTS.COMMENTS.REACT(id),
        method: 'POST',
        body: { emoji },
      }),
      invalidatesTags: (result, error, { adId }) => [{ type: 'Comments', id: adId }],
    }),
    getCommentsByAd: builder.query<PaginatedApiResponse<CommentWithUser[]>, { adId: string; page?: number; limit?: number }>({
      query: ({ adId, page = 1, limit = 20 }) =>
        `${API_ENDPOINTS.COMMENTS.BY_AD(adId)}?page=${page}&limit=${limit}`,
      providesTags: (result, error, { adId }) => [{ type: 'Comments', id: adId }],
    }),
    createComment: builder.mutation<ApiResponse<CommentWithUser>, CreateCommentRequest>({
      query: ({ adId, ...body }) => ({
        url: API_ENDPOINTS.COMMENTS.CREATE(adId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { adId }) => [{ type: 'Comments', id: adId }],
    }),
    getTransactions: builder.query<PaginatedApiResponse<{
      id: string;
      walletId: string;
      type: string;
      amount: number;
      balance: number;
      description?: string;
      referenceId?: string;
      createdAt: string;
    }[]>, { page?: number; limit?: number; type?: string } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.type) searchParams.set('type', params.type);
        const qs = searchParams.toString();
        return `${API_ENDPOINTS.WALLET.TRANSACTIONS}${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Transactions'],
    }),
  }),
});

export const {
  useGetAdBySlugQuery,
  useLazyGetAdBySlugQuery,
  useGetRelatedAdsQuery,
  useGetAdsQuery,
  useGetMyAdsQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
  useCloseAdMutation,
  useReopenAdMutation,
  useToggleBidsVisibilityMutation,
  useCreateBidMutation,
  useAcceptBidMutation,
  useRejectBidMutation,
  useWithdrawBidMutation,
  useGetMyBidsQuery,
  useGetCommentsByAdQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useReactToCommentMutation,
  useCheckContactUnlockQuery,
  useUnlockContactMutation,
  useGetTransactionsQuery,
} = adsApi;
